import React, { useMemo, useState } from 'react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Nav from 'react-bootstrap/Nav'
import Table from 'react-bootstrap/Table'
import ApplyPanel from './ApplyPanel'
import Attribution from './Attribution'
import Pagination from '../../../component/Pagination'
import useDecisions from './useDecisions'

// The course-name review queue.
//
// Split by confidence rather than shown as one list, deliberately. The first
// attempt at this exported about a thousand rows into one flat file and stalled,
// because there is no way to judge any single row without reading the whole wall.
// The buckets are what fixed that, so they are structural here — not a filter you
// can switch off.
const BUCKETS = [
  {
    key: 'AUTO',
    label: 'Automatic',
    // Highest-risk automatic source: it strips a trailing word off a course name,
    // and a wrong one takes a real word off a real place.
    hint: 'Applied without a human once you click. Read the LAYOUT_QUALIFIER_STRIP rows in full on a first pass.',
  },
  {
    key: 'REVIEW',
    label: 'Review',
    hint: 'The human queue, and deliberately only this.',
  },
  {
    key: 'NEEDS_EXTERNAL_LOOKUP',
    label: 'Needs lookup',
    hint: 'Nothing in our own data can fix these. Counted so "is scraping worth it" has a number behind it — do not drain it by loosening a rule.',
  },
]

export default function NamesPass({ country, data, onDryRun, onApply, applying, dryRunSeen }) {
  const [bucket, setBucket] = useState('AUTO')
  const [sourceFilter, setSourceFilter] = useState('')
  const [page, setPage] = useState(0)
  const { decisions, setDecision, decidedCount, clear } = useDecisions('names', country)

  const inBucket = useMemo(
    () =>
      (data?.proposals || []).filter(
        (row) =>
          row.confidence === bucket &&
          (!sourceFilter || row.source === sourceFilter)
      ),
    [data, bucket, sourceFilter]
  )

  const sources = useMemo(
    () =>
      [...new Set((data?.proposals || [])
        .filter((row) => row.confidence === bucket)
        .map((row) => row.source))].sort(),
    [data, bucket]
  )

  if (!data) return null

  const decisionFor = (row) =>
    decisions[row.courseId] ?? {
      newName: row.proposedName || '',
      newSecondaryName: row.proposedSecondaryName ?? null,
    }

  // The AUTO bucket is meant to be accepted in bulk — in the runner it was applied
  // without anyone clicking at all, because the alternative was a thousand manual
  // rows. Here the click is free, so it is a click; but it must not be a thousand
  // of them.
  const acceptAllShown = () => {
    inBucket.forEach((row) => {
      if (!row.proposedName) return
      setDecision(row.courseId, {
        newName: row.proposedName,
        newSecondaryName: row.proposedSecondaryName ?? null,
      })
    })
  }

  const clearShown = () => inBucket.forEach((row) => setDecision(row.courseId, undefined))

  const submit = (dryRun) => {
    const payload = Object.entries(decisions).map(([courseId, value]) => {
      const row = data.proposals.find((p) => String(p.courseId) === String(courseId))
      return {
        courseId: Number(courseId),
        oldName: row ? row.oldName : '',
        newName: value.newName,
        // Sent only when it is a real decision. Omitting the field means "leave the
        // existing qualifier alone"; sending "" means "clear it". Conflating those
        // silently blanks the qualifier on every accepted row.
        ...(value.newSecondaryName === null
          ? {}
          : { newSecondaryName: value.newSecondaryName }),
      }
    })
    ;(dryRun ? onDryRun : onApply)(payload, dryRun ? undefined : clear)
  }

  return (
    <>
      <div className="curation-counts mb-2">
        <Badge bg="secondary" className="me-2">auto {data.counts.auto}</Badge>
        <Badge bg="secondary" className="me-2">review {data.counts.review}</Badge>
        <Badge bg="secondary" className="me-2">needs lookup {data.counts.needsLookup}</Badge>
        <Badge bg="light" text="dark" className="me-2">left alone {data.counts.leaveAlone}</Badge>
        <Badge bg="light" text="dark" className="me-2">scorecards {data.counts.scorecards}</Badge>
        <Badge bg="light" text="dark">placeholders {data.counts.placeholders}</Badge>
      </div>

      <Nav variant="pills" activeKey={bucket} onSelect={(k) => { setBucket(k); setPage(0); setSourceFilter('') }}>
        {BUCKETS.map((b) => (
          <Nav.Item key={b.key}>
            <Nav.Link eventKey={b.key}>
              {b.label}{' '}
              <Badge bg="light" text="dark">
                {(data.proposals || []).filter((r) => r.confidence === b.key).length}
              </Badge>
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <p className="text-muted small mt-2">{BUCKETS.find((b) => b.key === bucket)?.hint}</p>

      <div className="d-flex gap-2 mb-2">
        <Button size="sm" variant="outline-primary" onClick={acceptAllShown}>
          Accept all {inBucket.length} shown
        </Button>
        <Button size="sm" variant="outline-secondary" onClick={clearShown}>
          Clear
        </Button>
      </div>

      {sources.length > 1 && (
        <Form.Select
          size="sm"
          className="mb-2 curation-source-filter"
          value={sourceFilter}
          onChange={(e) => { setSourceFilter(e.target.value); setPage(0) }}
        >
          <option value="">All sources</option>
          {sources.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Form.Select>
      )}

      <Pagination items={inBucket} page={page} onPage={setPage}>
        {(rows) => (
          <Table size="sm" responsive hover className="curation-table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Current name</th>
                <th>Proposed name</th>
                <th>Qualifier</th>
                <th>Source</th>
                <th>Why</th>
                <th>Layouts</th>
                <th>Catalogue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const decision = decisionFor(row)
                const decided = decisions[row.courseId] !== undefined
                return (
                  <tr key={row.courseId} className={decided ? 'table-active' : undefined}>
                    <td>{row.courseId}</td>
                    <td>
                      {row.oldName}
                      {row.oldSecondaryName && (
                        <span className="text-muted"> · {row.oldSecondaryName}</span>
                      )}
                    </td>
                    <td className="curation-name-cell">
                      {/* A textarea rather than an input: a course name runs to 100 characters and
                          this is the cell the decision is made in, so it has to show all of it. */}
                      <Form.Control
                        as="textarea"
                        rows={2}
                        size="sm"
                        value={decision.newName}
                        placeholder="blank to leave alone"
                        onChange={(e) =>
                          setDecision(row.courseId, { ...decision, newName: e.target.value })
                        }
                      />
                    </td>
                    <td>{row.layoutQualifier}</td>
                    <td className="small">{row.source}</td>
                    <td className="small text-muted curation-secondary-col">{row.evidence}</td>
                    <td className="small text-muted curation-secondary-col">
                      {row.layoutNames.join(' | ')}
                    </td>
                    <td className="small text-muted">
                      {row.catalogueName && (
                        <>
                          {row.catalogueName}
                          {row.catalogueLocality && <> ({row.catalogueLocality})</>}
                          {row.catalogueMetres != null && <> · {row.catalogueMetres} m</>}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Pagination>

      <Attribution text={data.attribution} />

      <ApplyPanel
        decidedCount={decidedCount}
        total={data.proposals.length}
        dryRunSeen={dryRunSeen}
        applying={applying}
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
